import {
  FormattedHTMLMessage,
  FormattedMessage,
  defineMessages,
  injectIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import { FloatingModal, PromptModal } from '../../components/Modal';
import { getMyOrdersForEvent } from '../../../../ducks/myorders';
import { navigate } from '../../../../helpers';
import Loader from '../../components/Loader';
import Option from '../components/Option';
import dhid from '../../../../dhid';
import styles from '../seatmap.scss';
import withUser from '../../../../components/withUser';

const isTimeRestricted = (ticket) => (
  ticket && ticket.ticket_type && ticket.ticket_type.restricts
    && ticket.ticket_type.restricts.available_from
    && !moment().isAfter(moment(`${ticket.ticket_type.restricts.available_from}+00`))
    && ticket.ticket_type.restricts.available_from
);

const formatTimeRestrict = (time) => (
  moment(`${time}+00`).format('h:mm A [on the] Do [of] MMMM YYYY').toString()
);

const propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      type: PropTypes.string.isRequired,
      eventId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  dispatch: PropTypes.func.isRequired,
  orders: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })),
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    shortcode: PropTypes.string,
  }),
  loading: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
};

const messages = defineMessages({
  okGotIt: {
    id: 'seatmap.tickets.index.okGotIt',
    defaultMessage: 'Ok, got it!',
  },
});

class Tickets extends React.Component {
  constructor() {
    super();
    this.state = {
      lockedModalOpen: false,
      giftedModalOpen: false,
      timeModalOpen: false,
      timeRestrict: undefined,
    };
  }

  componentDidMount() {
    const {
      dispatch,
      user,
      event,
    } = this.props;

    if (user && user.id && event) {
      dispatch(getMyOrdersForEvent(event.id));
    }
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      event,
      user,
    } = this.props;
    const { event: oldEvent, user: oldUser } = prevProps;

    if ((user && !oldUser) || (!oldEvent && event) || (oldEvent && oldEvent.id !== event.id)) {
      dispatch(getMyOrdersForEvent(event.id));
    }
  }

  onPressTicket = (ticket) => {
    const { history, match: { params } } = this.props;
    const { eventId, type } = params;

    if (ticket.locked) {
      this.openLockedModal();
    } else if (ticket.gifted) {
      this.openGiftedModal(ticket);
    } else if (ticket.noSeat && type === 'place') {
      // Just disable click
    } else if (isTimeRestricted(ticket) && type !== 'gift') {
      this.openTimeModal(ticket);
    } else if (type === 'place') {
      navigate(history, `/tickets/${eventId}/place/${ticket.id}`);
    } else if (type === 'gift') {
      navigate(history, `/tickets/${eventId}/gift/${ticket.id}`);
    }
  }

  openLockedModal = () => {
    this.setState({
      lockedModalOpen: true,
    });
  }

  closeLockedModal = () => {
    this.setState({
      lockedModalOpen: false,
    });
  }

  openTimeModal = (ticket) => {
    this.setState({
      timeModalOpen: true,
      timeRestrict: isTimeRestricted(ticket),
    });
  }

  closeTimeModal = () => {
    this.setState({
      timeModalOpen: false,
      timeRestrict: undefined,
    });
  }

  openGiftedModal = (ticket) => {
    this.setState({
      giftedModalOpen: true,
      giftedTicket: ticket,
    });
  }

  closeGiftedModal = () => {
    this.setState({
      giftedModalOpen: false,
      giftedTicket: undefined,
    });
  }

  render() {
    const {
      match,
      loading,
      orders,
      intl,
      history,
    } = this.props;
    const {
      lockedModalOpen,
      giftedModalOpen,
      timeModalOpen,
      timeRestrict,
      giftedTicket,
    } = this.state;
    const type = match && match.params && match.params.type;
    const { formatMessage } = intl;

    const orderTickets = orders.reduce((ret, order) => ([
      ...ret,
      ...order.all_tickets,
    ]), []);

    const allTickets = Object.values(orderTickets.reduce((ret, ticket) => ({
      ...ret,
      [ticket.id]: ticket,
    }), {}));

    const tickets = orders?.length > 0 && allTickets.map(({ order, ...ticket }) => ({
      ...ticket,
      order,
      gifted: order.claimed_by !== dhid.getUserID(),
      giftedTo: order.email,
      noSeat: ticket.ticket_type.restricts && ticket.ticket_type.restricts.seat_types
        && ticket.ticket_type.restricts.seat_types?.length === 0,
    }))
      .sort((a, b) => a.ticket_type.name.localeCompare(b.ticket_type.name));

    return (
      <>
        <Loader loader={loading} />
        { !loading && type === 'place'
          && (
            <>
              <div className={styles.title}>
                <FormattedMessage
                  id="seatmap.tickets.index.place.title"
                  defaultMessage="Which ticket would you like to place?"
                />
              </div>
              <div className={styles.description}>
                <FormattedHTMLMessage
                  id="seatmap.tickets.index.place.description"
                  defaultMessage="You currently have <strong>{ticketCount, number}</strong> {ticketCount, plural, one {ticket} other {tickets}}. Which one of them would you like to choose a seat for?"
                  values={{
                    ticketCount: tickets?.length || 0,
                  }}
                />
              </div>
            </>
          )}
        { !loading && type === 'gift'
          && (
            <>
              <div className={styles.title}>
                <FormattedMessage
                  id="seatmap.tickets.index.gift.title"
                  defaultMessage="Which ticket would you like to give away?"
                />
              </div>
              <div className={styles.description}>
                <FormattedHTMLMessage
                  id="seatmap.tickets.index.gift.description"
                  defaultMessage="You currently have <strong>{ticketCount, number}</strong> {ticketCount, plural, one {ticket} other {tickets}}. Which one of them would you like to give away?"
                  values={{
                    ticketCount: tickets?.length || 0,
                  }}
                />
              </div>
            </>
          )}
        {
          !loading && tickets && tickets.map((ticket) => (
            <Option
              key={ticket.id}
              icon={ticket.locked ? 'lock' : 'ticket-alt'}
              title={ticket.ticket_type && ticket.ticket_type.name}
              onClick={() => this.onPressTicket(ticket)}
              disabled={
                ticket.gifted || ticket.locked || (!!isTimeRestricted(ticket) && type !== 'gift')
                  || (type === 'place' && ticket.noSeat)
              }
            >
              {ticket?.upgrades && (
                <div className={styles.upgrades}>
                  {Object.values(ticket.upgrades || {}).map((u) => (
                    <div className={styles.upgrade}><i className="far fa-plus" />  {u.name}</div>
                  ))}
                </div>
              )}

              { ticket.locked && (
              <FormattedMessage
                id="seatmap.tickets.index.ticketLocked"
                defaultMessage="This ticket is locked."
              />
              )}
              { ticket.expanded_seat
                && (
                <FormattedMessage
                  id="seatmap.tickets.index.ticketPlacedAt"
                  defaultMessage="This ticket is placed at {rowName}:{seatName} in {floorName}."
                  values={{
                    rowName: ticket.expanded_seat.expanded_row.name,
                    seatName: ticket.expanded_seat.name,
                    // If we're looking at a ticket for montreal 19 - use section name as floor name
                    floorName: ticket.order.event_id === 'e04cdf59-11b4-4040-bb87-c25ca8678851'
                      ? ticket.expanded_seat.expanded_row.expanded_section.name
                      : ticket.expanded_seat.expanded_row.expanded_section.floor.name,
                  }}
                />
                )}
              { ticket.noSeat
                && (
                <FormattedMessage
                  id="seatmap.tickets.index.noReservedSeating"
                  defaultMessage="This ticket doesn't have reserved seating."
                />
                )}
              { !ticket.expanded_seat && !ticket.noSeat
                && (
                <FormattedMessage
                  id="seatmap.tickets.index.notPlacedYet"
                  defaultMessage="This ticket has not been placed yet."
                />
                )}
              { !ticket.noSeat && !ticket.locked && !ticket.gifted && (isTimeRestricted(ticket) && type !== 'gift')
                && (
                <FormattedHTMLMessage
                  id="seatmap.tickets.index.isTimeRestricted"
                  defaultMessage="You can choose a seat starting <strong>{timestamp}</strong>."
                  values={{
                    timestamp: formatTimeRestrict(isTimeRestricted(ticket)),
                  }}
                />
                )}
              { ticket.gifted
                && (
                <FormattedMessage
                  id="seatmap.tickets.index.hasBeenGifted"
                  defaultMessage="You have gifted this ticket to {email}."
                  values={{
                    email: ticket.giftedTo,
                  }}
                />
                )}
            </Option>
          ))
        }
        <FloatingModal
          isOpen={lockedModalOpen}
          onRequestClose={() => this.closeLockedModal()}
          buttonText={formatMessage(messages.okGotIt)}
        >
          <div className={styles.modal}>
            <div className={styles.title}>
              <FormattedMessage
                id="seatmap.tickets.index.locked.title"
                defaultMessage="This ticket is locked"
              />
            </div>
            <div className={styles.description}>
              <FormattedMessage
                id="seatmap.tickets.index.locked.description"
                defaultMessage="This can happen for a bunch of reasons, but usually it is when we lock the seatmap a few days prior to the event. If you missed the opportunity to choose a seat, your seat will automatically be assigned. Do you think this is a mistake? Please contact us at support@dreamhack.com."
              />
            </div>
          </div>
        </FloatingModal>
        <PromptModal
          isOpen={giftedModalOpen}
          onRequestClose={() => this.closeGiftedModal()}
          buttonText={formatMessage(messages.okGotIt)}
          title={(
            <FormattedMessage
              id="seatmap.tickets.index.gifted.title"
              defaultMessage="This ticket has been gifted"
            />
)}
          message={(
            <FormattedHTMLMessage
              id="seatmap.tickets.index.gifted.description"
              defaultMessage="You have given away this ticket to someone else and can therefore not change it anymore. Having troubles? Contact us at support@dreamhack.com."
            />
)}
          // Only show the "Take back the gift" action if the gifted order hasn't been claimed
          action={giftedTicket && !giftedTicket.order.claimed_by
            ? {
              onClick: () => navigate(history, `/tickets/${giftedTicket.order.event_id}/${giftedTicket.order.code}/connect`),
              text: <FormattedMessage
                id="seatmap.tickets.index.gifted.takeBack"
                defaultMessage="Take back the gift"
              />,
            }
            : null}
        />
        <FloatingModal
          isOpen={timeModalOpen}
          onRequestClose={() => this.closeTimeModal()}
          buttonText={formatMessage(messages.okGotIt)}
        >
          <div className={styles.modal}>
            <div className={styles.title}>
              <FormattedMessage
                id="seatmap.tickets.index.timelock.title"
                defaultMessage="Soon™"
              />
            </div>
            <div className={styles.description}>
              <FormattedHTMLMessage
                id="seatmap.tickets.index.timelock.description"
                defaultMessage="The option to choose seats for this ticket type will open up at <strong>{timestamp}</strong>. Be sure to check back then!"
                values={{
                  timestamp: formatTimeRestrict(timeRestrict),
                }}
              />
            </div>
          </div>
        </FloatingModal>
      </>
    );
  }
}

Tickets.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    event: state.getIn(['event', 'data']),
    loading: state.getIn(['myorders', 'loading']),
    orders: state.getIn(['myorders', 'orders']),
  };
}

export default injectIntl(withUser(connect(mapStateToProps)(Tickets)));
